import { useState, useEffect } from 'react'
import theme from '../theme/theme'

// Define general type for useWindowSize hook, which includes width and height
interface Size {
  width: number | undefined
  height: number | undefined
  upXs: boolean | undefined
  upSm: boolean | undefined
  upMd: boolean | undefined
  upLg: boolean | undefined
  upXl: boolean | undefined
  up2Xl: boolean | undefined
}

// Usage
// function App() {
//   const size: Size = useWindowSize()
//   return (
//     <div>
//       { size.width } px / { size.height }px
//     </div>
//   )
// }

// Hook
// eslint-disable-next-line import/prefer-default-export
export function useWindowSize(): Size {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState<Size>({
    width: undefined,
    height: undefined,
    upXs: undefined,
    upSm: undefined,
    upMd: undefined,
    upLg: undefined,
    upXl: undefined,
    up2Xl: undefined,
  })
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
        upXs: window.innerWidth > theme.breakpoints.values.xs,
        upSm: window.innerWidth > theme.breakpoints.values.sm,
        upMd: window.innerWidth > theme.breakpoints.values.md,
        upLg: window.innerWidth > theme.breakpoints.values.lg,
        upXl: window.innerWidth > theme.breakpoints.values.xl,
        up2Xl: window.innerWidth > theme.breakpoints.values['2xl'],
      })
    }

    // Add event listener
    window.addEventListener('resize', handleResize)
    // Call handler right away so state gets updated with initial window size
    handleResize()
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize)
  }, []) // Empty array ensures that effect is only run on mount
  return windowSize
}
